import React, { useState } from "react";
import styled from "@emotion/styled";
import { LogIn, Eye, EyeOff, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { useTheme } from "../../context/ThemeContext";
import { useAuthStore } from "../../store/authStore";

interface LoginFormProps {
  onLoginSuccess: (user: any) => void;
}

const Wrapper = styled.div<{ $isDarkMode: boolean }>`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => (p.$isDarkMode ? "#1a1d23" : "#f5f5f5")};
  padding: 24px;
`;

const Card = styled.div<{ $isDarkMode: boolean }>`
  width: 100%;
  max-width: 420px;
  background: ${(p) => (p.$isDarkMode ? "#2c2f36" : "#ffffff")};
  border: 1px solid ${(p) => (p.$isDarkMode ? "#3a3f47" : "#e5e7eb")};
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, ${(p) => (p.$isDarkMode ? 0.35 : 0.08)});
  padding: 28px;
`;

const Header = styled.div<{ $isDarkMode: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  color: ${(p) => (p.$isDarkMode ? "#e9ecef" : "#1f2937")};
`;

const IconBubble = styled.div<{ $isDarkMode: boolean }>`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: ${(p) => (p.$isDarkMode ? "rgba(255,255,255,0.08)" : "#e6f0ff")};
  color: ${(p) => (p.$isDarkMode ? "#8ab4ff" : "#2563eb")};
`;

const Title = styled.h2<{ $isDarkMode: boolean }>`
  font-size: 22px;
  font-weight: 700;
  margin: 0;
  color: ${(p) => (p.$isDarkMode ? "#f1f5f9" : "#111827")};
`;

const Subtitle = styled.p<{ $isDarkMode: boolean }>`
  font-size: 14px;
  margin: 0;
  color: ${(p) => (p.$isDarkMode ? "#adb5bd" : "#6b7280")};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label<{ $isDarkMode: boolean }>`
  font-size: 13px;
  color: ${(p) => (p.$isDarkMode ? "#cbd5e1" : "#374151")};
`;

const InputWrapper = styled.div`
  position: relative;
`;

const Input = styled.input<{ $isDarkMode: boolean }>`
  width: 100%;
  padding: 10px 12px;
  padding-right: 40px;
  border: 1px solid ${(p) => (p.$isDarkMode ? "#3a3f47" : "#d1d5db")};
  background: ${(p) => (p.$isDarkMode ? "#1f232a" : "#ffffff")};
  color: ${(p) => (p.$isDarkMode ? "#e5e7eb" : "#111827")};
  border-radius: 8px;
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: ${(p) => (p.$isDarkMode ? "#8ab4ff" : "#2563eb")};
    box-shadow: 0 0 0 3px ${(p) => (p.$isDarkMode ? "rgba(138,180,255,0.25)" : "rgba(37,99,235,0.15)")};
  }
`;

const PasswordToggle = styled.button<{ $isDarkMode: boolean }>`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => (p.$isDarkMode ? "#94a3b8" : "#6b7280")};
  cursor: pointer;
`;

const SubmitButton = styled.button<{ $isDarkMode: boolean }>`
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 10px 14px;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  background: linear-gradient(135deg, #4f46e5 0%, #2563eb 100%);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.1s ease, opacity 0.2s ease;

  &:hover {
    opacity: 0.95;
  }
  &:active {
    transform: translateY(1px);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const HelperText = styled.p<{ $isDarkMode: boolean }>`
  text-align: center;
  font-size: 12px;
  color: ${(p) => (p.$isDarkMode ? "#94a3b8" : "#6b7280")};
  margin: 8px 0 0 0;
`;

export const LoginForm: React.FC<LoginFormProps> = ({ onLoginSuccess }) => {
  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { isDarkMode } = useTheme();
  const { login } = useAuthStore();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPin(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!pin.trim()) {
      toast.error("Please enter your PIN");
      return;
    }

    if (pin.length < 12) {
      toast.error("PIN must be at least 12 characters");
      return;
    }

    setIsLoading(true);

    try {
      await login({ pin });
      const user = useAuthStore.getState().user;
      if (user) {
        toast.success(`Welcome back!`);
        onLoginSuccess(user);
      }
    } catch (error) {
      console.error("Login failed:", error);
      toast.error("Invalid PIN. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Wrapper $isDarkMode={isDarkMode}>
      <Card $isDarkMode={isDarkMode}>
        <Header $isDarkMode={isDarkMode}>
          <IconBubble $isDarkMode={isDarkMode}>
            <LogIn size={22} />
          </IconBubble>
          <Title $isDarkMode={isDarkMode}>Secure Access</Title>
          <Subtitle $isDarkMode={isDarkMode}>Service Diagram Editor - PIN Authentication</Subtitle>
        </Header>

        <Form onSubmit={handleSubmit}>
          <Field>
            <Label htmlFor="pin" $isDarkMode={isDarkMode}>
              Access PIN
            </Label>
            <InputWrapper>
              <Input id="pin" name="pin" type={showPin ? "text" : "password"} autoComplete="off" value={pin} onChange={handleInputChange} required disabled={isLoading} $isDarkMode={isDarkMode} placeholder="Enter your secure PIN" minLength={12} />
              <PasswordToggle type="button" onClick={() => setShowPin(!showPin)} disabled={isLoading} $isDarkMode={isDarkMode} aria-label={showPin ? "Hide PIN" : "Show PIN"}>
                {showPin ? <EyeOff size={18} /> : <Eye size={18} />}
              </PasswordToggle>
            </InputWrapper>
          </Field>

          <SubmitButton type="submit" disabled={isLoading || !pin.trim() || pin.length < 12} $isDarkMode={isDarkMode}>
            {isLoading ? <Loader2 className="animate-spin" size={16} /> : <LogIn size={16} />}
            Sign in
          </SubmitButton>

          <HelperText $isDarkMode={isDarkMode}>Internal tool access only. Use your secure PIN (min. 12 characters) to access.</HelperText>
        </Form>
      </Card>
    </Wrapper>
  );
};
